'use client'

import { useSearchPropertyPopup } from '@/context/search-property-popup'
import { useGSAP } from '@gsap/react'
import { Collapse, type CollapseProps } from 'antd'
import gsap from 'gsap'
import {
  ChartLine,
  ChevronDown,
  ChevronUp,
  DollarSign,
  FileChartColumn,
} from 'lucide-react'
import Image from 'next/image'
import { useRef, useState } from 'react'
import Button from '../button'
import FooterFullScreen from '../footer/FooterFullScreen'

export default function TheProblem() {
  const iconColor = '#ffffff'

  const rootContainerRef = useRef<HTMLDivElement>(null)
  const theProblemSectionRef = useRef<HTMLDivElement>(null)
  const theProblemLeftSectionRef = useRef<HTMLDivElement>(null)
  const theProblemRightSectionRef = useRef<HTMLDivElement>(null)
  const cardPhoto2ImgRef = useRef<HTMLDivElement>(null)
  const cardPhoto2CloneRef = useRef<HTMLDivElement>(null)
  const whatWeDoSectionContainerRef = useRef<HTMLDivElement>(null)
  const whatWeDoSectionRef = useRef<HTMLDivElement>(null)
  const whatWeDoTextContentRef = useRef<HTMLDivElement>(null)
  const whatWeDoButtonRef = useRef<HTMLDivElement>(null)
  const whatWeDoStatsContainerRef = useRef<HTMLDivElement>(null)
  const fileChartIconRef = useRef<HTMLDivElement>(null)
  const dollarSignIconRef = useRef<HTMLDivElement>(null)
  const chartLineIconRef = useRef<HTMLDivElement>(null)
  const annualRepresentationRef = useRef<HTMLDivElement>(null)
  const annualRepresentationContentRef = useRef<HTMLDivElement>(null)
  const buildingImgDivRef = useRef<HTMLDivElement>(null)
  const howItWorkRef = useRef<HTMLDivElement>(null)
  const howItWorkContentRef = useRef<HTMLDivElement>(null)
  const howItWorkTheProcessContentRef = useRef<HTMLDivElement>(null)
  const howItWorkFAQContentRef = useRef<HTMLDivElement>(null)
  const neverMissPropertyTaxSectionRef = useRef<HTMLDivElement>(null)
  const footerSectionRef = useRef<HTMLDivElement>(null)
  const mainTimelineRef = useRef<gsap.core.Timeline | null>(null)
  const mainScrollTriggerRef = useRef<gsap.core.Tween['scrollTrigger'] | null>(
    null,
  )

  const videoRef = useRef<HTMLVideoElement>(null)
  const [activeStep, setActiveStep] = useState(0)
  const [isDesktopPinned, setIsDesktopPinned] = useState(false)
  const sectionLabels = [
    'the-problem.end',
    'what-we-do.end',
    'annual-representation.end',
    'how-it-works.end',
    'never-miss.end',
    'footer.end',
  ] as const

  const { setValue } = useSearchPropertyPopup()

  const collapseItemClassName =
    'bg-[#E2DBD8] py-2 md:py-4 lg:py-8 border-b! border-[#877C77]!'

  const collapseItemClassNames = {
    header: 'text-[14px] md:text-[20px] font-bold text-[#877C77]!',
    body: 'text-[12px] md:text-[16px] text-[#877C77]!',
  }

  const howItWorkFAQCollapseLeftItems: CollapseProps['items'] = [
    {
      key: '1',
      label: 'What does it cost?',
      children:
        'We work on a 30 percent contingency fee. You only pay if we successfully reduce your property taxes. No savings. No fee.',
      classNames: collapseItemClassNames,
      className: collapseItemClassName,
    },
    {
      key: '2',
      label: 'How much can I save?',
      children:
        'Savings vary by property. Our average reduction success rate is 91 percent, with average annual savings of approximately 1,200 dollars. Every property is reviewed individually to maximize potential reduction.',
      classNames: collapseItemClassNames,
      className: collapseItemClassName,
    },
    {
      key: '3',
      label: 'Do I need to enroll every year?',
      children:
        'No. You enroll once. We automatically review and file each year so you never miss a deadline or overpay again.',
      classNames: collapseItemClassNames,
      className: collapseItemClassName,
    },
  ]

  const howItWorkFAQCollapseRightItems: CollapseProps['items'] = [
    {
      key: '1',
      label: 'What if my value decreased this year?',
      children:
        'Even when values decline, they may still exceed true market or equal and uniform value. We review and protest annually to ensure no savings are left on the table.',
      classNames: collapseItemClassNames,
      className: collapseItemClassName,
    },
    {
      key: '2',
      label: 'Will I need to attend a hearing?',
      children:
        'No. We represent you directly before the appraisal district and handle all communication on your behalf.',
      classNames: collapseItemClassNames,
      className: collapseItemClassName,
    },
    {
      key: '3',
      label: 'When will I know how much I can save?',
      children:
        'Most protests are finalized within three to four months, typically around September. Scheduling is determined by the appraisal district, and we keep you informed throughout the process.',
      classNames: collapseItemClassNames,
      className: collapseItemClassName,
    },
  ]

  function animateWhatWeDoSectionForLGBreakPoint({
    tl,
    whatWeDoSectionFadeOutStart,
    whatWeDoSectionFadeOutDuration,
    neverMissPropertyTaxSectionTweenDuration,
    neverMissPropertyTaxSectionStart,
  }: {
    tl: gsap.core.Timeline
    whatWeDoSectionFadeOutStart: number
    whatWeDoSectionFadeOutDuration: number
    neverMissPropertyTaxSectionTweenDuration: number
    neverMissPropertyTaxSectionStart: number
  }) {
    tl.to(
      [
        chartLineIconRef.current,
        whatWeDoTextContentRef.current,
        whatWeDoButtonRef.current,
      ],
      {
        y: -window.innerHeight,
        ease: 'power3.inOut',
        duration: whatWeDoSectionFadeOutDuration,
      },
      whatWeDoSectionFadeOutStart,
    )
    tl.to(
      fileChartIconRef.current,
      {
        x: -window.innerWidth,
        y: -window.innerHeight / 2,
        ease: 'power3.inOut',
        duration: whatWeDoSectionFadeOutDuration,
      },
      whatWeDoSectionFadeOutStart,
    )
    tl.to(
      dollarSignIconRef.current,
      {
        x: window.innerWidth,
        y: -window.innerHeight / 2,
        ease: 'power3.inOut',
        duration: whatWeDoSectionFadeOutDuration,
      },
      whatWeDoSectionFadeOutStart,
    )

    // move what we do section up and fade out while playing video
    tl.to(
      whatWeDoSectionRef.current,
      {
        minWidth: '100vw',
        minHeight: '100vh',
        ease: 'power3.inOut',
        duration: whatWeDoSectionFadeOutDuration,
      },
      whatWeDoSectionFadeOutStart - 500,
    )

    tl.to(
      buildingImgDivRef.current,
      {
        inset: 0,
        width: '100svw',
        height: '100svh',
        duration: neverMissPropertyTaxSectionTweenDuration - 500,
        ease: 'power3.inOut',
      },
      neverMissPropertyTaxSectionStart - 500,
    )
  }

  function animateWhatWeDoSectionForXLBreakPoint({
    tl,
    whatWeDoSectionFadeOutStart,
    whatWeDoSectionFadeOutDuration,
    neverMissPropertyTaxSectionTweenDuration,
    neverMissPropertyTaxSectionStart,
  }: {
    tl: gsap.core.Timeline
    whatWeDoSectionFadeOutStart: number
    whatWeDoSectionFadeOutDuration: number
    neverMissPropertyTaxSectionTweenDuration: number
    neverMissPropertyTaxSectionStart: number
  }) {
    tl.to(
      whatWeDoTextContentRef.current,
      {
        x: -window.innerWidth,
        duration: whatWeDoSectionFadeOutDuration,
        ease: 'power3.inOut',
      },
      whatWeDoSectionFadeOutStart,
    )

    tl.to(
      whatWeDoStatsContainerRef.current,
      {
        x: window.innerWidth,
        duration: whatWeDoSectionFadeOutDuration,
        ease: 'power3.inOut',
      },
      whatWeDoSectionFadeOutStart,
    )

    // move what we do section up and fade out while playing video
    tl.to(
      whatWeDoSectionRef.current,
      {
        maxWidth: '100vw',
        maxHeight: '100vh',
        width: '100vw',
        height: '100vh',
        ease: 'power3.inOut',
        duration: whatWeDoSectionFadeOutDuration,
      },
      whatWeDoSectionFadeOutStart,
    )

    tl.to(
      buildingImgDivRef.current,
      {
        width: '100svw',
        duration: neverMissPropertyTaxSectionTweenDuration - 500,
        ease: 'power3.inOut',
      },
      neverMissPropertyTaxSectionStart - 500,
    )
  }

  type AnimateForDesktopParams = {
    tl: gsap.core.Timeline
    theProblemSectionTweenDuration: number
    whatWeDoSectionStart: number
    whatWeDoSectionTweenDuration: number
    whatWeDoSectionFadeOutDuration: number
    whatWeDoSectionFadeOutStart: number
    annualRepresentationStart: number
    annualRepresentationDuration: number
    howItWorkSectionStart: number
    howItWorkSectionTweenDuration: number
    howItWorkTheProcessSectionEnd: number
    howItWorkFAQSectionStart: number
    howItWorkFAQSectionDuration: number
    neverMissPropertyTaxSectionStart: number
    neverMissPropertyTaxSectionTweenDuration: number
    footerFadeInStart: number
    footerTweenDuration: number
  }

  function animateCommon(params: AnimateForDesktopParams) {
    const {
      tl,
      theProblemSectionTweenDuration,
      whatWeDoSectionStart,
      whatWeDoSectionTweenDuration,
      whatWeDoSectionFadeOutDuration,
      whatWeDoSectionFadeOutStart,
      annualRepresentationStart,
      annualRepresentationDuration,
      howItWorkSectionStart,
      howItWorkSectionTweenDuration,
      howItWorkFAQSectionDuration,
      howItWorkFAQSectionStart,
      howItWorkTheProcessSectionEnd,
      neverMissPropertyTaxSectionStart,
      neverMissPropertyTaxSectionTweenDuration,
      footerFadeInStart,
      footerTweenDuration,
    } = params

    tl.fromTo(
      theProblemLeftSectionRef.current,
      {
        x: -window.innerWidth,
      },
      {
        scrollTrigger: {
          trigger: rootContainerRef.current,
          start: '5% bottom',
          end: `+=${theProblemSectionTweenDuration}`,
          scrub: true,
          pin: false,
        },
        x: 0,
        ease: 'power3.inOut',
      },
      0,
    )

    tl.fromTo(
      theProblemRightSectionRef.current,
      {
        x: window.innerWidth,
      },
      {
        scrollTrigger: {
          trigger: rootContainerRef.current,
          start: '5% bottom',
          end: `+=${theProblemSectionTweenDuration}`,
          scrub: true,
          pin: false,
        },
        x: 0,
        ease: 'power3.inOut',
        onComplete: () => {
          setClonedImgPosition()
        },
      },
      0,
    )

    // region what we do section

    const clonedImg = cardPhoto2CloneRef.current!

    tl.to(
      clonedImg,
      {
        display: 'block',
        duration: 1,
      },
      whatWeDoSectionStart,
    )

    tl.to(
      clonedImg,
      {
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        ease: 'power3.in',
        duration: whatWeDoSectionTweenDuration,
      },
      whatWeDoSectionStart,
    )

    tl.fromTo(
      whatWeDoSectionRef.current,
      {
        display: 'grid',
        scale: 0.5,
        autoAlpha: 0,
        zIndex: 60,
      },
      {
        scale: 1,
        autoAlpha: 1,
        duration: whatWeDoSectionTweenDuration - 500,
        delay: 1000,
      },
      whatWeDoSectionStart,
    )

    // endregion what we do section

    // region annual representation section

    tl.to(
      clonedImg,
      {
        opacity: 0,
        ease: 'power3.inOut',
        duration: whatWeDoSectionFadeOutDuration,
      },
      whatWeDoSectionFadeOutStart + 5000,
    )

    // end fadeout

    tl.fromTo(
      annualRepresentationRef.current,
      {
        opacity: 0,
      },
      {
        display: 'flex',
        opacity: 1,
        duration: annualRepresentationDuration,
      },
      annualRepresentationStart,
    )

    tl.fromTo(
      annualRepresentationContentRef.current,
      {
        y: window.innerHeight,
      },
      {
        y: 0,
        opacity: 1,
        ease: 'power3.out',
        duration: annualRepresentationDuration,
      },
      annualRepresentationStart,
    )

    const video = videoRef.current!
    const videoDuration = 4000
    video.pause()

    const videoProxy = { progress: 0 }

    tl.to(
      videoProxy,
      {
        progress: 1,
        duration: videoDuration,
        ease: 'none',
        onUpdate: () => {
          if (video.duration) {
            video.currentTime = videoProxy.progress * video.duration
          }
        },
      },
      annualRepresentationStart,
    )

    // endregion annual representation section

    // region how it works section

    // fade out annual representation section
    tl.to(
      annualRepresentationRef.current,
      {
        yPercent: -100,
        ease: 'power3.out',
        duration: howItWorkSectionTweenDuration,
      },
      howItWorkSectionStart,
    )
    // end fade out annual representation section

    tl.set(
      buildingImgDivRef.current,
      {
        x: -window.innerWidth,
      },
      0,
    )

    tl.set(
      howItWorkTheProcessContentRef.current,
      {
        x: window.innerWidth,
        height: '100svh',
      },
      0,
    )

    tl.set(
      howItWorkRef.current,
      {
        display: 'grid',
      },
      howItWorkSectionStart,
    )

    tl.to(
      buildingImgDivRef.current,
      {
        x: 0,
        ease: 'power3.inOut',
        duration: howItWorkSectionTweenDuration,
      },
      howItWorkSectionStart,
    )

    tl.to(
      howItWorkTheProcessContentRef.current,
      {
        x: 0,
        ease: 'power3.inOut',
        duration: howItWorkSectionTweenDuration,
      },
      howItWorkSectionStart,
    )

    tl.to(
      howItWorkTheProcessContentRef.current,
      {
        y: -window.innerHeight,
        ease: 'power3.out',
        duration: 1500,
      },
      howItWorkTheProcessSectionEnd,
    )

    tl.set(
      howItWorkFAQContentRef.current,
      {
        height: '100svh',
        y: window.innerHeight,
      },
      0,
    )

    tl.to(
      howItWorkFAQContentRef.current,
      {
        y: 0,
        ease: 'power3.out',
        duration: howItWorkFAQSectionDuration,
        delay: 400,
      },
      howItWorkFAQSectionStart,
    )

    // endregion how it works section

    // region never miss a property tax protest again section

    tl.set(
      neverMissPropertyTaxSectionRef.current,
      {
        translateX: `-${window.innerWidth + 500}px`,
        translateY: '-50%',
      },
      0,
    )

    tl.to(
      howItWorkFAQContentRef.current,
      {
        x: window.innerWidth,
        ease: 'power3.inOut',
        duration: neverMissPropertyTaxSectionTweenDuration - 500,
      },
      neverMissPropertyTaxSectionStart - 500,
    )

    tl.to(
      neverMissPropertyTaxSectionRef.current,
      {
        translateX: '-50%',
        translateY: '-50%',
        ease: 'power3.inOut',
        duration: neverMissPropertyTaxSectionTweenDuration,
      },
      neverMissPropertyTaxSectionStart - 700,
    )

    // endregion never miss a property tax protest again section

    // region footer fade in

    tl.fromTo(
      footerSectionRef.current,
      {
        y: window.innerHeight,
      },
      {
        y: 0,
        ease: 'power3.inOut',
        duration: footerTweenDuration,
      },
      footerFadeInStart,
    )

    // endregion footer fade in
  }

  function animateForMobile() {
    const theProblemSectionTweenDuration = 1.2
    gsap.fromTo(
      theProblemLeftSectionRef.current,
      {
        xPercent: -150,
      },
      {
        xPercent: 0,
        duration: theProblemSectionTweenDuration,
        scrollTrigger: {
          trigger: theProblemSectionRef.current,
          start: '20% bottom',
          toggleActions: 'play none none reset',
        },
      },
    )

    gsap.fromTo(
      theProblemRightSectionRef.current,
      {
        xPercent: 150,
      },
      {
        xPercent: 0,
        delay: 0.3,
        duration: theProblemSectionTweenDuration,
        scrollTrigger: {
          trigger: theProblemSectionRef.current,
          start: '20% bottom',
          toggleActions: 'play none none reset',
        },
      },
    )

    gsap.fromTo(
      whatWeDoSectionRef.current,
      {
        autoAlpha: 0,
        scale: 0.5,
      },
      {
        autoAlpha: 1,
        scale: 1,
        scrollTrigger: {
          trigger: whatWeDoSectionContainerRef.current,
          start: '40% bottom',
          duration: 3,
          toggleActions: 'play none none reset',
        },
      },
    )

    gsap.fromTo(
      annualRepresentationContentRef.current,
      {
        y: window.innerHeight,
      },
      {
        y: 0,
        scrollTrigger: {
          trigger: annualRepresentationRef.current,
          start: '40% bottom',
          duration: 3,
          toggleActions: 'play none none reset',
        },
        onStart: () => {
          // play video when animation starts
          videoRef.current?.play()
        },
      },
    )

    gsap.fromTo(
      buildingImgDivRef.current,
      {
        xPercent: -100,
      },
      {
        xPercent: 0,
        scrollTrigger: {
          trigger: howItWorkRef.current,
          start: '30% bottom',
          duration: 3,
          toggleActions: 'play none none reset',
        },
      },
    )

    gsap.fromTo(
      howItWorkContentRef.current,
      {
        xPercent: 100,
      },
      {
        xPercent: 0,
        scrollTrigger: {
          trigger: howItWorkRef.current,
          start: '40% bottom',
          duration: 3,
          toggleActions: 'play none none reset',
        },
      },
    )

    gsap.fromTo(
      neverMissPropertyTaxSectionRef.current,
      {
        xPercent: -100,
      },
      {
        xPercent: 0,
        scrollTrigger: {
          trigger: neverMissPropertyTaxSectionRef.current,
          start: '40% bottom',
          duration: 3,
          toggleActions: 'play none none reset',
        },
      },
    )
  }

  function setClonedImgPosition() {
    const originalImg = cardPhoto2ImgRef.current!
    const clonedImg = cardPhoto2CloneRef.current!

    const cloneImgRect = originalImg.getBoundingClientRect()
    gsap.set(clonedImg, {
      position: 'fixed',
      top: cloneImgRect.top,
      left: cloneImgRect.left,
      width: cloneImgRect.width,
      height: cloneImgRect.height,
      zIndex: 50,
    })
  }

  function smoothScrollTo(position: number) {
    if (!Number.isFinite(position)) {
      return
    }

    gsap.to(window, {
      scrollTo: { y: position, autoKill: false },
      duration: 1,
      overwrite: 'auto',
      ease: 'none',
    })
  }

  function getStepScrollY(step: number) {
    const tl = mainTimelineRef.current
    const st = mainScrollTriggerRef.current
    if (!tl || !st) return null

    const label = sectionLabels[step]
    let targetY = st.start

    if (typeof st.labelToScroll === 'function') {
      targetY = st.labelToScroll(label)
    } else {
      const totalDuration = tl.totalDuration() || 1
      const labelTime = tl.labels[label] ?? 0
      const progress = Math.max(0, Math.min(labelTime / totalDuration, 1))
      targetY = st.start + (st.end - st.start) * progress
    }

    return gsap.utils.clamp(st.start, st.end, targetY)
  }

  function goToSection(step: number) {
    const targetStep = Math.max(0, Math.min(step, sectionLabels.length - 1))
    setActiveStep(targetStep)

    const desktopScrollY = getStepScrollY(targetStep)
    if (desktopScrollY !== null && isDesktopPinned) {
      smoothScrollTo(desktopScrollY)
      return
    }

    const sectionRefs = [
      theProblemSectionRef,
      whatWeDoSectionContainerRef,
      annualRepresentationRef,
      howItWorkRef,
      neverMissPropertyTaxSectionRef,
      footerSectionRef,
    ] as const

    const targetEl = sectionRefs[targetStep]?.current
    if (!targetEl) {
      return
    }

    const targetTop = targetEl.getBoundingClientRect().top + window.scrollY
    smoothScrollTo(targetTop)
  }

  function handleSectionNavigationNext() {
    goToSection(activeStep + 1)
  }

  function handleSectionNavigationPrev() {
    goToSection(activeStep - 1)
  }

  useGSAP(
    () => {
      function buildTimeline(
        pin: boolean,
        isXL: boolean,
        isMobile: boolean = false,
      ) {
        const tl = gsap.timeline({
          scrollTrigger: {
            trigger: rootContainerRef.current,
            start: 'top top',
            end: `+=17200`,
            scrub: pin,
            pin,
            invalidateOnRefresh: true,
            onUpdate: (self) => {
              if (!pin || !mainTimelineRef.current) {
                return
              }

              const currentTl = mainTimelineRef.current
              const currentTime = self.progress * currentTl.totalDuration()

              const closestStep = sectionLabels.reduce(
                (acc, label, index) => {
                  const labelTime = currentTl.labels[label] ?? 0
                  const distance = Math.abs(labelTime - currentTime)

                  if (distance < acc.distance) {
                    return { index, distance }
                  }

                  return acc
                },
                { index: 0, distance: Number.POSITIVE_INFINITY },
              )

              setActiveStep(closestStep.index)
            },
          },
          defaults: {
            ease: 'none',
          },
        })

        setIsDesktopPinned(pin)
        mainTimelineRef.current = tl
        mainScrollTriggerRef.current = tl.scrollTrigger ?? null

        const theProblemSectionTweenDuration = 2000

        // region what we do section

        // const whatWeDoSectionStart = theProblemSectionTweenDuration - 1200
        const whatWeDoSectionStart = theProblemSectionTweenDuration
        const whatWeDoSectionTweenDuration = 2000
        const whatWeDoSectionEnd =
          whatWeDoSectionStart +
          whatWeDoSectionTweenDuration +
          whatWeDoSectionTweenDuration / 2

        // endregion what we do section

        // region annual representation section

        const whatWeDoSectionFadeOutStart = whatWeDoSectionEnd + 500
        const whatWeDoSectionFadeOutDuration = 2000

        const annualRepresentationStart =
          whatWeDoSectionFadeOutStart + whatWeDoSectionFadeOutDuration - 1000

        const annualRepresentationDuration = 3000
        const annualRepresentationEnd =
          annualRepresentationStart + annualRepresentationDuration

        // endregion annual representation section

        // region how it works section

        const howItWorkSectionStart = annualRepresentationEnd
        const howItWorkSectionTweenDuration = 2500
        const howItWorkTheProcessSectionEnd =
          howItWorkSectionStart + howItWorkSectionTweenDuration

        const howItWorkFAQSectionStart = howItWorkTheProcessSectionEnd
        const howItWorkFAQSectionDuration = 2500

        const howItWorkSectionEnd =
          howItWorkFAQSectionStart + howItWorkFAQSectionDuration

        // endregion how it works section

        // region never miss a property tax protest again section

        const neverMissPropertyTaxSectionStart = howItWorkSectionEnd
        const neverMissPropertyTaxSectionTweenDuration = 1500
        const neverMissPropertyTaxSectionEnd =
          neverMissPropertyTaxSectionStart +
          neverMissPropertyTaxSectionTweenDuration

        // endregion never miss a property tax protest again section

        // region footer fade in
        const footerFadeInStart = neverMissPropertyTaxSectionEnd
        const footerTweenDuration = 1500
        const footerFadeInEnd = footerFadeInStart + footerTweenDuration

        // endregion footer fade in

        tl.addLabel('the-problem.end', theProblemSectionTweenDuration)
        tl.addLabel('what-we-do.end', whatWeDoSectionEnd)
        tl.addLabel('annual-representation.end', annualRepresentationEnd)
        tl.addLabel('how-it-works.end', howItWorkTheProcessSectionEnd)
        tl.addLabel('never-miss.end', neverMissPropertyTaxSectionEnd)
        tl.addLabel('footer.end', footerFadeInEnd)

        if (isMobile) {
          animateForMobile()
        } else if (isXL) {
          animateCommon({
            tl,
            theProblemSectionTweenDuration,
            whatWeDoSectionStart,
            whatWeDoSectionTweenDuration,
            whatWeDoSectionFadeOutDuration,
            whatWeDoSectionFadeOutStart,
            annualRepresentationStart,
            annualRepresentationDuration,
            howItWorkSectionStart,
            howItWorkSectionTweenDuration,
            howItWorkFAQSectionDuration,
            howItWorkFAQSectionStart,
            howItWorkTheProcessSectionEnd,
            neverMissPropertyTaxSectionStart,
            neverMissPropertyTaxSectionTweenDuration,
            footerFadeInStart,
            footerTweenDuration,
          })

          animateWhatWeDoSectionForXLBreakPoint({
            tl,
            whatWeDoSectionFadeOutDuration,
            whatWeDoSectionFadeOutStart,
            neverMissPropertyTaxSectionTweenDuration,
            neverMissPropertyTaxSectionStart,
          })
        } else {
          animateCommon({
            tl,
            theProblemSectionTweenDuration,
            whatWeDoSectionStart,
            whatWeDoSectionTweenDuration,
            whatWeDoSectionFadeOutDuration,
            whatWeDoSectionFadeOutStart,
            annualRepresentationStart,
            annualRepresentationDuration,
            howItWorkSectionStart,
            howItWorkSectionTweenDuration,
            howItWorkFAQSectionDuration,
            howItWorkFAQSectionStart,
            howItWorkTheProcessSectionEnd,
            neverMissPropertyTaxSectionStart,
            neverMissPropertyTaxSectionTweenDuration,
            footerFadeInStart,
            footerTweenDuration,
          })
          animateWhatWeDoSectionForLGBreakPoint({
            tl,
            whatWeDoSectionFadeOutDuration,
            whatWeDoSectionFadeOutStart,
            neverMissPropertyTaxSectionTweenDuration,
            neverMissPropertyTaxSectionStart,
          })
        }

        return () => {
          mainTimelineRef.current = null
          mainScrollTriggerRef.current = null
          tl.kill()
        }
      }

      const mm = gsap.matchMedia()

      // mobile — no pinning
      mm.add('(max-width: 767px)', () => {
        return buildTimeline(false, false, true)
      })

      // md/lg — pin, LG-style animation
      mm.add('(min-width: 768px) and (max-width: 1279px)', () => {
        return buildTimeline(true, false)
      })

      // xl+ — pin, XL-style animation
      mm.add('(min-width: 1280px)', () => {
        return buildTimeline(true, true)
      })

      return () => {
        mm.revert()
      }
    },
    { scope: rootContainerRef },
  )

  return (
    <section
      ref={rootContainerRef}
      className="bg-[#E2DBD8] min-h-svh overflow-x-hidden md:overflow-hidden md:flex md:items-center md:justify-center relative"
    >
      {/* the problem section */}
      <div
        ref={theProblemSectionRef}
        className="w-full grid grid-cols-12 gap-6 lg:gap-10 items-center min-h-svh ps-[24px] md:ps-[60px] xl:ps-[100px]
        py-[24px] xl:py-[36px] pe-[20px] lg:pe-[40px]"
      >
        <div
          ref={theProblemLeftSectionRef}
          className="col-span-12 md:col-span-6"
        >
          <div className="max-w-[677px] flex flex-col gap-[1rem] md:gap-[1.1rem]">
            <p className="uppercase text-[12px] md:text-[14px] font-bold tracking-[8px]">
              THE PROBLEM
            </p>
            <h1 className="text-xl md:text-[32px] lg:text-[48px] max-w-[1240px] uppercase leading-tight">
              Many Texas Properties Are Over-Assessed.
            </h1>
            <p className="text-sm md:text-lg lg:text-[1.25rem]">
              Appraisal values do not always reflect true market conditions. You
              may be paying more than required under Texas law.
            </p>
            <div className="text-sm md:text-base lg:text-[1.125rem] text-justify flex flex-col gap-[0.7rem]">
              <p>
                In Texas, counties value thousands of properties each year using
                mass appraisal systems. These systems rely on broad market
                trends and statistical models to assign values, but they cannot
                always capture the unique details of your property.
              </p>
              <p>
                That means your property’s assessed value may not fully reflect
                its condition, features, or local market nuances. Even small
                differences can impact the taxes you pay each year.
              </p>
              <p>
                That is why we review every property individually. We analyze
                comparable sales, property condition, and relevant valuation
                factors to ensure your assessment is properly evaluated. With
                professional representation, you have an advocate in the protest
                process. No upfront fees, and only pay if a reduction is
                achieved.
              </p>
            </div>
            <div className="font-bold">
              <p className="text-sm md:text-lg lg:text-[22px]">
                &ldquo;Pinnacle reduced our assessed value and handled the
                entire process seamlessly. We did not lift a finger.&rdquo;
              </p>
              <p className="text-xs md:text-sm lg:text-[16px]">
                —Austin Homeowner, Travis County
              </p>
            </div>
            <Button
              text="Protect My Property Today"
              variant="solid"
              className="w-fit pointer-events-auto"
              onClick={() => setValue(true)}
            />
          </div>
        </div>
        <div
          ref={theProblemRightSectionRef}
          className="col-span-12 md:col-span-6 md:h-full"
        >
          <div className="h-full flex md:flex-col gap-4 lg:gap-10 items-start justify-center">
            <div className="relative w-full max-w-[323px] lg:max-w-[503px] h-[25vh] lg:h-[283px] md:ml-6 xl:ml-15">
              <Image
                src="/images/card-photo.png"
                alt="card photo"
                fill
                className="object-cover"
                priority
              />
            </div>
            <div
              ref={cardPhoto2ImgRef}
              className="relative w-full max-w-[323px] lg:max-w-[503px] h-[25vh] lg:h-[283px]"
            >
              <Image
                src="/images/card-photo2.png"
                alt="card photo 2"
                fill
                className="object-cover"
                priority
              />
            </div>
          </div>
        </div>
      </div>
      {/* end of the problem section */}

      {/* Clone image for viewport animation */}
      <div
        ref={cardPhoto2CloneRef}
        className="overflow-hidden pointer-events-none hidden"
        style={{ willChange: 'top, left, width, height' }}
      >
        <Image
          src="/images/card-photo2.png"
          alt="card photo 2"
          fill
          className="object-cover"
          priority
        />
      </div>

      {/*what we do section*/}
      <section
        ref={whatWeDoSectionContainerRef}
        className="relative md:absolute md:inset-0 md:z-[9999] w-full h-dvh md:flex md:justify-center pointer-events-none"
      >
        <div className="md:hidden absolute pointer-events-none inset-0 z-[5]">
          <Image
            src="/images/card-photo2.png"
            alt="card photo 2"
            fill
            className="object-cover"
            priority
          />
        </div>
        <section
          ref={whatWeDoSectionRef}
          className="absolute inset-0 z-[10] grid grid-cols-1 xl:grid-cols-12 items-center
        content-center gap-x-12 gap-y-10 xl:gap-y-12 w-full md:max-w-[90%] h-fit xl:max-h-[29rem]
        py-10 p-[1.5rem] md:p-[3rem] xl:p-[5rem] bg-[#E2DBD8F2]/97 m-auto"
        >
          <div ref={whatWeDoTextContentRef} className="xl:col-span-8">
            <div className="max-w-[677px] mx-auto xl:mx-0 text-[#0E0E10] flex flex-col gap-y-4 xl:gap-y-8 text-center xl:text-left">
              <p className="font-bold text-[12px] tracking-[8px] uppercase">
                WHAT WE DO
              </p>
              <h1 className="text-4xl md:text-[2.5rem] lg:text-[3rem] max-w-[1240px] uppercase">
                Only Pay when you Save
              </h1>
              <p className="text-[18px] ">
                Texas homeowners pay some of the highest property taxes in
                America. Below this use these stats, with the numbers LARGEST in
                the middle of the stat please. Then in columns or numbered row
                pending formatting on your end,
              </p>
              <div ref={whatWeDoButtonRef}>
                <Button
                  text="Get Professional Representation"
                  variant="solid"
                  className="pointer-events-auto"
                  onClick={() => setValue(true)}
                />
              </div>
            </div>
          </div>

          <div
            ref={whatWeDoStatsContainerRef}
            className="xl:col-span-4 text-[#E2DBD8] xl:overflow-y-hidden w-full"
          >
            <div className="grid grid-cols-3 xl:grid-cols-1 gap-2 xl:gap-y-8">
              <div
                ref={fileChartIconRef}
                className="w-full flex flex-col gap-y-2 xl:gap-y-4 items-center bg-[#877C77] p-3 xl:p-[2.5rem] border-2 border-[#E2DBD8]"
              >
                <i>
                  <FileChartColumn
                    size={36}
                    color={iconColor}
                    strokeWidth={1}
                  />
                </i>
                <span className="text-xs xl:text-[20px] text-center">
                  In 2025, less than half of all residential properties filed
                  protests
                </span>
              </div>
              <div
                ref={chartLineIconRef}
                className="w-full flex flex-col gap-y-2 xl:gap-y-4 items-center bg-[#877C77] p-3 xl:p-[2.5rem] border-2 border-[#E2DBD8]"
              >
                <i>
                  <ChartLine size={36} color={iconColor} strokeWidth={1} />
                </i>
                <span className="text-xs xl:text-[20px] text-center">
                  Average reduction success rate is 91%
                </span>
              </div>
              <div
                ref={dollarSignIconRef}
                className="w-full flex flex-col gap-y-2 xl:gap-y-4 items-center bg-[#877C77] p-3 xl:p-[2.5rem] border-2 border-[#E2DBD8]"
              >
                <i>
                  <DollarSign size={36} color={iconColor} strokeWidth={1} />
                </i>
                <span className="text-xs xl:text-[20px] text-center">
                  Average $1,200 savings
                </span>
              </div>
            </div>
          </div>
        </section>
      </section>
      {/*endof what we do section*/}

      {/* annual representation section */}
      <section
        ref={annualRepresentationRef}
        className="relative md:absolute md:inset-0 md:z-[99999] h-dvh flex items-center justify-center bg-[#E2DBD8] pointer-events-none"
      >
        <video
          ref={videoRef}
          src="/videos/hand-shake-filtered-bw.mp4"
          className="absolute z-10 w-svw h-svh object-contain xl:object-cover"
          preload="auto"
          muted
          playsInline
        />
        {/*bg overlay*/}
        <div className="absolute z-15 bg-[#E2DBD8]/60 w-svw h-svh"></div>
        <div
          ref={annualRepresentationContentRef}
          className="absolute z-20 w-full max-w-[677px] flex flex-col items-center gap-y-12"
        >
          <div className="flex flex-col gap-y-8 text-center px-5">
            <p className="uppercase font-bold text-[12px] tracking-[8px] ">
              ANNUAL REPRESENTATION
            </p>
            <h1 className="text-2xl md:text-4xl lg:text-[3.3rem] max-w-[1240px] uppercase">
              Enroll Once. We Handle It Every Year.
            </h1>
            <p className="text-[18px]">
              Appraisal values do not always reflect true market conditions.You
              may be paying more than required under Texas law.
            </p>
          </div>
          <Button
            text="Start Annual Representation"
            className="pointer-events-auto"
            onClick={() => setValue(true)}
          />
        </div>
      </section>
      {/* endof annual representation section */}

      {/* section how it works */}

      <section
        ref={howItWorkRef}
        className="md:hidden h-fit md:h-dvh grid grid-cols-1 md:grid-cols-12 gap-y-4 md:absolute md:z-[9999] inset-0 bg-[#E2DBD8]"
      >
        <div
          ref={buildingImgDivRef}
          className="cols-1 md:col-span-4 relative h-[20vh] md:h-dvh"
        >
          <Image
            src="/images/texas-state-capital-building.webp"
            alt="building"
            fill
            className="object-cover object-center"
            priority
          />
        </div>
        <div
          ref={howItWorkContentRef}
          className="cols-1 md:col-span-8 relative min-h-fit md:max-h-svh md:overflow-y-hidden"
        >
          {/* THE PROCESS */}
          <div
            ref={howItWorkTheProcessContentRef}
            className="flex flex-col md:justify-center px-8 md:px-16 lg:px-24"
          >
            <div className="flex flex-col gap-y-4 mb-12 lg:pl-14">
              <p className="text-[12px] tracking-[8px] font-bold uppercase">
                HOW IT WORKS
              </p>
              <h1 className="text-2xl md:text-4xl lg:text-[3.3rem] max-w-[741px] uppercase leading-tight">
                How the Process Works.
              </h1>
            </div>

            <div className="flex flex-col gap-y-3 md:gap-y-8 mb-12">
              {/* Step 1 */}
              <div className="flex items-start gap-x-6">
                <div className="flex-shrink-0 w-16 h-16 rounded-full border border-[#C5BFBB] flex items-center justify-center">
                  <span className="text-3xl text-[#545457] font-light">1</span>
                </div>
                <div className="flex flex-col gap-y-1 pt-2">
                  <h2 className="text-xl md:text-2xl font-bold uppercase">
                    Find Your Property
                  </h2>
                  <p className="text-[16px]">
                    Search your property to get started.
                  </p>
                </div>
              </div>

              {/* Step 2 */}
              <div className="flex items-start gap-x-6">
                <div className="flex-shrink-0 w-16 h-16 rounded-full border border-[#C5BFBB] flex items-center justify-center">
                  <span className="text-3xl text-[#545457] font-light">2</span>
                </div>
                <div className="flex flex-col gap-y-1 pt-2">
                  <h2 className="text-xl md:text-2xl font-bold uppercase">
                    Sign Once
                  </h2>
                  <p className="text-[16px]">
                    Complete your authorization electronically.
                  </p>
                </div>
              </div>

              {/* Step 3 */}
              <div className="flex items-start gap-x-6">
                <div className="flex-shrink-0 w-16 h-16 rounded-full border border-[#C5BFBB] flex items-center justify-center">
                  <span className="text-3xl text-[#545457] font-light">3</span>
                </div>
                <div className="flex flex-col gap-y-1 pt-2">
                  <h2 className="text-xl md:text-2xl font-bold uppercase">
                    We Handle the Protest Every Year
                  </h2>
                  <p className="text-[16px]">
                    We review and file your protest annually to ensure no
                    savings are missed.
                  </p>
                </div>
              </div>
            </div>

            <div className="lg:pl-14">
              <Button
                text="File My Protest"
                variant="solid"
                className="w-fit"
                onClick={() => setValue(true)}
              />
            </div>
          </div>

          {/* FAQ */}
          <div
            ref={howItWorkFAQContentRef}
            className="md:absolute md:inset-0 flex flex-col md:justify-center mt-10 md:mt-0 px-8 md:px-16 lg:px-24 md:py-12 bg-[#E2DBD8]"
          >
            <div className="flex flex-col gap-y-4 mb-3 md:mb-12">
              <p className="text-[12px] tracking-[8px] font-bold uppercase">
                HOW IT WORKS
              </p>
              <h1 className="text-2xl md:text-4xl lg:text-[3rem] max-w-[741px] uppercase leading-tight">
                Frequently Asked Questions
              </h1>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 md:gap-x-6">
              <Collapse
                items={howItWorkFAQCollapseLeftItems}
                bordered={false}
                className="!bg-[#e2dbd8] faq-collapse"
              />
              <Collapse
                items={howItWorkFAQCollapseRightItems}
                bordered={false}
                className="!bg-[#e2dbd8] faq-collapse"
              />
            </div>
          </div>
        </div>
      </section>

      {/* endof section how it works */}

      {/* never miss a property tax section */}
      <section
        ref={neverMissPropertyTaxSectionRef}
        className="relative md:absolute md:z-[9999] md:top-1/2 md:left-1/2 w-full md:max-w-[600px] lg:max-w-[798px] h-dvh"
      >
        <div className="md:hidden relative w-full h-svh">
          <Image
            src="/images/texas-state-capital-building.webp"
            alt="building"
            fill
            className="object-cover object-center"
            priority
          />
        </div>
        <div className="absolute m-auto h-fit inset-0 flex flex-col gap-y-4 md:gap-y-8 items-center justify-center p-10 bg-[#E2DBD8E5]/90">
          <h1 className="text-[40px] md:text-[60px] lg:text-[70px] xl:text-[80px] font-[400] uppercase text-center">
            Never Miss a Property Tax Protest Again.
          </h1>
          <div className="flex flex-col lg:flex-row items-center justify-center gap-4">
            <Button text="File My Protest" onClick={() => setValue(true)} />
            <Button
              text="Learn How Annual Representation Works"
              variant="outlined"
              onClick={() => setValue(true)}
            />
          </div>
        </div>
      </section>
      {/* endof never miss a property tax section */}

      <div
        ref={footerSectionRef}
        className="md:absolute inset-0 z-[99999] w-svw h-dvh"
      >
        <FooterFullScreen />
      </div>

      <div className="fixed right-4 bottom-1/2 z-[100000] hidden md:flex flex-col gap-2">
        <button
          type="button"
          aria-label="Go to previous section"
          onClick={handleSectionNavigationPrev}
          disabled={activeStep === 0}
          className="h-11 w-11 rounded-full border border-[#877C77] bg-[#E2DBD8] text-[#0E0E10] shadow-sm flex items-center justify-center transition-all disabled:opacity-40 cursor-pointer disabled:cursor-not-allowed"
        >
          <ChevronUp size={20} />
        </button>
        <button
          type="button"
          aria-label="Go to next section"
          onClick={handleSectionNavigationNext}
          disabled={activeStep === sectionLabels.length - 1}
          className="h-11 w-11 rounded-full border border-[#877C77] bg-[#E2DBD8] text-[#0E0E10] shadow-sm flex items-center justify-center transition-all disabled:opacity-40 cursor-pointer disabled:cursor-not-allowed"
        >
          <ChevronDown size={20} />
        </button>
      </div>
    </section>
  )
}
